import { createGlobalConfig, panoramaClientFactory, PanoramaPlatform, PanoramaClient } from '@wix/panorama-client-web';

// Shared with the testkit; client creation stays browser-only because components also render on the server.
export const globalConfig = createGlobalConfig();
let panoramaClient: PanoramaClient | undefined;

export const flushPanorama = () => {
  try {
    // The reference apps use this optional queue API to retain reports across navigation.
    (globalConfig.getBatchQueue() as unknown as { flush?: () => void } | undefined)?.flush?.();
  } catch (_) {
    // Monitoring must not interrupt navigation if the upstream queue API changes.
  }
};

export const getPanoramaClient = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (panoramaClient) {
    return panoramaClient;
  }
  const state = window.__INITIAL_STATE__;
  const userId = state?.userData?.isLoggedIn ? state.userData.userId : undefined;
  const sentry = window.Sentry;
  panoramaClient = panoramaClientFactory({
    baseParams: {
      platform: PanoramaPlatform.Standalone,
      fullArtifactId: 'com.wixpress.marketing.marketing-template-viewer',
      artifactVersion: window.__APP_VERSION__,
      componentId: 'marketing-template-viewer',
      uuid: userId,
    },

    pluginParams: { sentry, sentryDsn: window.__SENTRY_DSN__, sentryMain: true, sentryEnvironment: window.__ENV__ },
  })
    .withGlobalConfig(globalConfig)
    .client();

  // InboundFilters reads these options on each event, including Panorama's Sentry hub.
  const options = sentry?.getCurrentHub().getClient()?.getOptions() as
    | { ignoreErrors?: string[]; denyUrls?: RegExp[]; allowUrls?: string[]; release?: string }
    | undefined;
  if (options) {
    options.release = window.__APP_VERSION__;
    options.ignoreErrors = [
      'conductExperiment?key=specs.fedinfra.Glassbox',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'logMutedMessage',
      'Permission denied to access property "unlock"',
      'Could not establish connection. Receiving end does not exist.',
      'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',
    ];
    options.denyUrls = [
      /^(chrome|moz)-extension:\/\//,
      /safari-(web-)?extension/,
      /^webpack-internal:\/\//,
      /^\/\/hidden\//,
      /^<anonymous>/,
    ];
  }
  sentry?.configureScope((scope) => {
    scope.setUser({ id: userId });
    scope.setExtra('editorSessionId', state?.template?.editorSessionId);
    scope.setExtra('visitorId', window.__VISITOR_ID__);
  });
  sentry?.addGlobalEventProcessor((event) => {
    const value = event.exception?.values?.[0];
    return value?.type === 'UnhandledRejection' && !value.stacktrace ? null : event;
  });
  window.addEventListener('pagehide', flushPanorama);
  return panoramaClient;
};
